import classNames from "classnames";
import { includes, isEmpty, last } from "lodash";
import moment from "moment";
import AppContext from "PFApp/app_context";
import { useBookingModalContext } from "PFApp/booking/components/booking_modal/booking_modal_context_provider";
import { useMisalignments } from "PFApp/booking/parts/hooks";
import { CalendarMode, CalendarView, Row as RowAsType } from "PFApp/booking/types";
import { RowType } from "PFApp/booking/types/row";
import useBookingCategories from "PFCore/hooks/use_booking_categories";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import React, { memo, useCallback, useContext, useRef, useState } from "react";

import { BookingModalMode } from "../../../../components/booking_modal/use_booking_modal";
import { getOriginalResource } from "../../../helpers";
import { useBookingRowsContext } from "../../context/booking_rows_context";
import { useBookingsOverviewContext } from "../../context/bookings_overview_context";
import { useCalendarContext } from "../../context/calendar_context";
import { ExceptionDays, NominalDays } from "../days";
import { AvailabilityRow } from "./availability_row";
import { BookingsRow } from "./bookings_row";
import { getRowCalendarsData } from "./helpers";
import css from "./row.module.scss";
import { UtilizationRow } from "./utilization_row";

type RowComponent = typeof UtilizationRow | typeof AvailabilityRow | typeof BookingsRow;
const getRowComponent = (view: CalendarView): RowComponent => {
  switch (view) {
    case CalendarView.Utilization:
      return UtilizationRow;
    case CalendarView.Availability:
      return AvailabilityRow;
    default:
      return BookingsRow;
  }
};

type RowProps = {
  row: RowAsType;
  width: number;
  startDate: string;
  currentDate: string;
  endDate: string;
};

export const Row = memo(({ row, width, startDate, currentDate, endDate }: RowProps): JSX.Element => {
  const { dayWidth } = useCalendarContext();
  const { utc, formatISODate, formatDatabaseDateTime } = useDateFormatter();

  const {
    store: {
      currentAccount: { availability }
    }
  } = useContext(AppContext);
  const { past_days_limit: pastDaysLimit } = availability || {};
  const {
    calendarMode,
    splitMode,
    calendarData: { view }
  } = useBookingsOverviewContext();
  const { barsByRowKey, rowsByCalendarMode, rowDimensionsByCalendarMode } = useBookingRowsContext();

  const {
    modal: { open: openModal }
  } = useBookingModalContext();
  const { misalignmentBars } = useMisalignments({ row, mode: calendarMode });

  const rowRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [draggedBookingId, setDraggedBookingId] = useState<number | null>(null);
  const { bookingCategories } = useBookingCategories();
  const hasWriteAccessToAtLeastOneCategory = bookingCategories.some((i) => !i.readonly);
  const dimensions = rowDimensionsByCalendarMode[calendarMode][row.key];

  const rows = rowsByCalendarMode[calendarMode];
  const parentRow = rows.find(({ key }) => last(row.parents) === key);
  const rowCalendarsData = getRowCalendarsData(row, startDate, endDate, calendarMode, parentRow);
  const isRootRow = isEmpty(row.parents);
  const isFirstRow = rows?.[0]?.key === row.key;
  const isLastRow = last(rows)?.key === row.key;

  const handleClick = useCallback(
    (event, row: RowAsType) => {
      event.stopPropagation();
      if (calendarMode !== CalendarMode.Workforce || !hasWriteAccessToAtLeastOneCategory || splitMode) {
        return;
      }
      // offset from the left as we now support pass periods
      const daysFromStart = utc().diff(utc(startDate), "days");
      const relativeX = event.clientX - event.target.getBoundingClientRect().x - daysFromStart * dayWidth;
      const dayFromStartClicked = Math.floor(relativeX / dayWidth);
      const dateClicked = utc(currentDate).add(dayFromStartClicked, "days");
      const date = formatISODate(dateClicked);
      const start = formatDatabaseDateTime(dateClicked);

      const today = formatDatabaseDateTime(utc());
      const isDayAvailableToCreateBooking = moment(start).diff(today, "days") >= -1 * (pastDaysLimit ?? 0);

      if (isDayAvailableToCreateBooking) {
        const profileId = getOriginalResource(
          row.type !== RowType.Profile ? parentRow! : row,
          calendarMode
        )?.id;
        openModal(
          {
            profileId,
            bookings: [
              {
                startDate: date,
                endDate: date
              }
            ]
          },
          { mode: BookingModalMode.Create }
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [calendarMode, splitMode, dayWidth, startDate, utc, formatISODate, formatDatabaseDateTime]
  );

  const isSummary = calendarMode === CalendarMode.Workforce && row.type === RowType.Profile;
  const shouldBarsOverlap =
    (isSummary && view === CalendarView.Compressed) ||
    (Math.max((barsByRowKey[row.key] || []).length, misalignmentBars.length) < 2 &&
      !includes([CalendarView.Utilization, CalendarView.Availability], view));

  const RowComponent = getRowComponent(view);

  /* eslint-disable jsx-a11y/click-events-have-key-events */
  /* eslint-disable jsx-a11y/no-static-element-interactions */

  return (
    <div
      ref={rowRef}
      className={classNames(css.row, {
        [css.rowLoading]: loading,
        [css.centered]: shouldBarsOverlap,
        [css.utilizationView]:
          includes([CalendarView.Utilization, CalendarView.Availability], view) &&
          row.type === RowType.Profile,
        [css.borderTop]: isRootRow && !isFirstRow,
        [css.borderBottom]: isLastRow
      })}
      style={{
        ...(dimensions && {
          top: dimensions.top,
          height: dimensions.height
        }),
        width
      }}
      onClick={!draggedBookingId ? (event) => handleClick(event, row) : undefined}
    >
      {rowCalendarsData && (
        <>
          <NominalDays rowCalendarsData={rowCalendarsData} startDate={startDate} endDate={endDate} />
          <ExceptionDays rowCalendarsData={rowCalendarsData} startDate={startDate} dayWidth={dayWidth} />
        </>
      )}
      <RowComponent
        row={row}
        ref={rowRef}
        startDate={startDate}
        endDate={endDate}
        onLoadingChange={(value) => setLoading(value)}
        draggedBookingId={draggedBookingId}
        onBarDragStart={(bookingId) => setDraggedBookingId(bookingId)}
        onBarDragEnd={() => setDraggedBookingId(null)}
        shouldBarsOverlap={shouldBarsOverlap}
      />
    </div>
  );
});

Row.displayName = "Row";
